/**
 * Org ordering config (Plan 47) client-side data layer.
 *
 * Reads the per-org config from REST at `/api/org-ordering-config/*`
 * via TanStack Query. The runtime-gating layer in customer pages
 * (/orders/family, /orders/school, /cafeteria) hangs off these hooks.
 *
 * Server REST routes live at `apps/ordo/server/src/routes/org-ordering-config.ts`.
 * The matching manager + in-memory store lives at
 * `apps/ordo/server/src/managers/org-ordering-config.ts`.
 */

import { useQuery } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types — mirror the manager's wire shape
// ---------------------------------------------------------------------------

export type OrderingChannel = 'PARENT_ORDER' | 'SCHOOL_ORDER'
export type FulfillmentMode = 'PRE_ORDER' | 'POINT_OF_SALE'

export const ORDERING_CHANNELS: ReadonlyArray<OrderingChannel> = ['PARENT_ORDER', 'SCHOOL_ORDER']
export const FULFILLMENT_MODES: ReadonlyArray<FulfillmentMode> = ['PRE_ORDER', 'POINT_OF_SALE']
export const DEFAULT_LEAD_TIME_DAYS = 21

export interface OrgOrderingConfig {
  organizationId: string
  orderingChannels: OrderingChannel[]
  fulfillmentModes: FulfillmentMode[]
  preOrderLeadTimeDays: number
  isSeeded: boolean
}

// ---------------------------------------------------------------------------
// Pure helpers — duplicated against the server's `isChannelEnabled`/etc.
// so the client-side gating layer doesn't need to round-trip to ask
// "is PARENT_ORDER on?". Server-side validation is still the
// authoritative source for cart-submit rejections.
// ---------------------------------------------------------------------------

export function isChannelEnabled(config: OrgOrderingConfig | null | undefined, channel: OrderingChannel): boolean {
  if (!config) {
    // Loading / no-config = allow-all (graceful pre-Plan-48 default).
    return true
  }
  return config.orderingChannels.includes(channel)
}

export function isModeEnabled(config: OrgOrderingConfig | null | undefined, mode: FulfillmentMode): boolean {
  if (!config) {
    return true
  }
  return config.fulfillmentModes.includes(mode)
}

export function isPosOnly(config: OrgOrderingConfig | null | undefined): boolean {
  if (!config) {
    return false
  }
  return config.fulfillmentModes.includes('POINT_OF_SALE') && !config.fulfillmentModes.includes('PRE_ORDER')
}

/**
 * Builds the allow-all default the customer client uses when the
 * server-side config row hasn't been seeded yet. Equivalent to the
 * server's `defaultOrgOrderingConfig`.
 */
export function defaultOrgOrderingConfig(organizationId: string): OrgOrderingConfig {
  return {
    organizationId,
    orderingChannels: [...ORDERING_CHANNELS],
    fulfillmentModes: [...FULFILLMENT_MODES],
    preOrderLeadTimeDays: DEFAULT_LEAD_TIME_DAYS,
    isSeeded: false,
  }
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchOrgOrderingConfig(organizationId: string, signal?: AbortSignal): Promise<OrgOrderingConfig> {
  return apiFetch<OrgOrderingConfig>(`/api/org-ordering-config/${encodeURIComponent(organizationId)}`, { signal })
}

export interface OrgOrderingConfigsResponse {
  configs: OrgOrderingConfig[]
}

export async function fetchOrgOrderingConfigs(
  organizationIds: ReadonlyArray<string>,
  signal?: AbortSignal,
): Promise<OrgOrderingConfigsResponse> {
  return apiFetch<OrgOrderingConfigsResponse>('/api/org-ordering-config', {
    query: { organizationIds: organizationIds.join(',') },
    signal,
  })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const orgOrderingConfigQueryKey = (organizationId: string) => ['org-ordering-config', organizationId] as const

export const orgOrderingConfigsQueryKey = (organizationIds: ReadonlyArray<string>) =>
  ['org-ordering-config', 'bulk', [...organizationIds].sort().join(',')] as const

/**
 * Single-org read. Returns `null` while loading (which `isChannelEnabled`
 * / `isModeEnabled` treat as allow-all so the gating UI doesn't flash
 * a disabled banner before the config lands).
 */
export function useOrgOrderingConfigQuery(organizationId: string | null) {
  return useQuery<OrgOrderingConfig>({
    queryKey: orgOrderingConfigQueryKey(organizationId ?? ''),
    queryFn: ({ signal }) => fetchOrgOrderingConfig(organizationId!, signal),
    enabled: !!organizationId,
    // Org-level config changes infrequently — let TanStack serve it
    // from cache across page navigations within a session.
    staleTime: 5 * 60_000,
  })
}

/**
 * Bulk read for multi-school households (a parent with kids at >1
 * school). One round-trip; map by organizationId at the call site.
 */
export function useOrgOrderingConfigsQuery(organizationIds: ReadonlyArray<string>) {
  const sortedIds = [...organizationIds].sort()
  return useQuery<OrgOrderingConfigsResponse>({
    queryKey: orgOrderingConfigsQueryKey(sortedIds),
    queryFn: ({ signal }) => fetchOrgOrderingConfigs(sortedIds, signal),
    enabled: sortedIds.length > 0,
    staleTime: 5 * 60_000,
  })
}
